/**
 * SOURCE.2 — weapon-technique compatibility (source-weapon-technique-hybrid-contract.md
 * §19). Pure legality helpers only — no numeric
 * logic, no power credit for narrower compatibility (contract §2).
 */

import { WEAPON_FAMILIES, type SourceRequirement, type WeaponFamily, type WeaponTechnique } from '../types.js';

/**
 * Weapon-family capability matrix — a legality matrix, not a power multiplier.
 * Every family but RANGED swings and thrusts (owner decision 2026-10-18; the
 * bow shoots only). Which weapon of a family has the shape for it is the
 * contact geometry's call (`BALANCE_THRUST_GEOMETRY_V0`), not this matrix's.
 */
export const WEAPON_TECHNIQUE_CAPABILITIES_V1: Readonly<Record<WeaponFamily, readonly WeaponTechnique[]>> = Object.freeze({
  HEAVY: Object.freeze<WeaponTechnique[]>(['SWING', 'THRUST']),
  BALANCED: Object.freeze<WeaponTechnique[]>(['SWING', 'THRUST']),
  LIGHT: Object.freeze<WeaponTechnique[]>(['SWING', 'THRUST']),
  POLEARM: Object.freeze<WeaponTechnique[]>(['SWING', 'THRUST']),
  RANGED: Object.freeze<WeaponTechnique[]>(['SHOOT']),
  CATALYST: Object.freeze<WeaponTechnique[]>(['SWING', 'THRUST']),
});

export function weaponFamilySupportsTechnique(family: WeaponFamily, technique: WeaponTechnique): boolean {
  return WEAPON_TECHNIQUE_CAPABILITIES_V1[family].includes(technique);
}

/**
 * The effective legal source set: technique-compatible families intersected
 * with the requirement's explicit `families` restriction, when present
 * (contract §19). Returns `WEAPON_FAMILIES`'s own canonical declared order.
 */
export function compatibleWeaponFamilies(
  requirement: Extract<SourceRequirement, { sourceClass: 'WEAPON' }>,
): readonly WeaponFamily[] {
  const techniqueCompatible = WEAPON_FAMILIES.filter((family) => weaponFamilySupportsTechnique(family, requirement.technique));
  if (requirement.families === undefined) return techniqueCompatible;
  const restriction = new Set(requirement.families);
  return techniqueCompatible.filter((family) => restriction.has(family));
}
